import { useAeropayUser } from '@iheartjane/data-access/react-query';
import { AppMode } from '@iheartjane/dm-sdk';
import { getFlagContext } from '@iheartjane/feature-flags';
import { useWindowListener } from '@iheartjane/reefer';
import { statePostalCode } from '@iheartjane/utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import {
  ConfirmationScreen,
  CreateAccountScreen,
  ForgotPasswordScreen,
  SignInScreen,
  SignInToggleButtons,
  SuccessScreen,
} from '@jane/kiosk/components';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import {
  SCREEN_NAMES,
  useKioskAuth,
  useShouldKioskShowGold,
  useShowJanePayOnKiosk,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import { useSessionContext } from '@jane/kiosk/providers';
import type { SsoChannel } from '@jane/kiosk/util';
import {
  getUsername,
  trackAbandonedSignIn,
  trackBeginsSignIn,
  trackLinkBankAccountTap,
  trackRegistered,
  trackSignInTimeout,
  trackSignedIn,
} from '@jane/kiosk/util';
import { getJwt } from '@jane/shared/auth';
import { LoadingWrapper } from '@jane/shared/components';
import { useGetStore, useJaneUser } from '@jane/shared/data-access';
import type { Store } from '@jane/shared/types';

const getStep = (screen: string) => {
  if (screen === 'create-account') return 'Information';
  return 'Sign in';
};

export const LoginOnDevice = () => {
  const hasSentTrackingEvent = useRef(false);
  const hasSentTimeoutTrackingEvent = useRef(false);
  const hasSignedIn = useRef(false);

  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const [queryParams] = useSearchParams();
  const userCode = queryParams.get('user_code');
  const source = queryParams.get('source') || '';
  const handoff = queryParams.get('handoff');
  const isHandoffFlow = handoff === 'success';
  const navigate = useNavigate();
  const showGold = useShouldKioskShowGold();
  const showJanePay = useShowJanePayOnKiosk();
  const { flagClient, jdid } = useSessionContext();

  const { data: janeUser, isFetching: isUserFetching } = useJaneUser();
  const { data: aeropayData, isFetching: isFetchingAeropayData } =
    useAeropayUser({
      janeUserId: janeUser?.user?.id,
      jwt: getJwt(),
    });
  const userName = useMemo(
    () => (janeUser ? getUsername(janeUser.user) : ''),
    [janeUser]
  );
  const userHasLinkedBank = !!aeropayData?.bank_accounts.length;
  const needsMoreInfoForLinkBank =
    !janeUser?.user?.first_name ||
    !janeUser?.user?.last_name ||
    !janeUser?.user?.phone;

  const onLoginSuccess = (ssoChannel: SsoChannel) => {
    hasSignedIn.current = true;
    trackSignedIn({
      source: [source],
      ssoChannel,
      store,
    });
  };

  const onRegisterSuccess = (ssoChannel: SsoChannel) => {
    hasSignedIn.current = true;
    trackRegistered({
      source: [source],
      ssoChannel,
      store,
    });
  };

  const {
    loginError,
    registerErrors,
    resetPasswordError,
    forgotPasswordScreen,
    screen,
    setForgotPasswordScreen,
    handleSetScreen,
    onAppleLogin,
    onGoogleLogin,
    signInSubmit,
    createAccountSubmit,
    resetPasswordSubmit,
    logoutUser,
    onAuthSuccess: kioskDeviceAuthorization,
  } = useKioskAuth({
    onLoginSuccess,
    onRegisterSuccess,
    storeId,
    userCode: userCode ?? '',
  });

  useEffect(() => {
    if (store && source && !hasSentTrackingEvent.current) {
      trackBeginsSignIn({
        store,
        source: [source],
      });
      hasSentTrackingEvent.current = true;
    }
  }, [store, source]);

  /** @todo include "Verification" in abandoned tracking (figure out how to know we sent an mfa but it wasn't completed) */
  const handleBeforeUnload = useCallback(() => {
    if (!hasSignedIn.current) {
      trackAbandonedSignIn({
        step: getStep(screen ?? ''),
        store,
        source: [source],
      });
    }
  }, [screen, source, store]);

  useEffect(() => {
    if (
      loginError.includes('Your link has expired') &&
      !hasSentTimeoutTrackingEvent.current
    ) {
      hasSentTimeoutTrackingEvent.current = true;
      trackSignInTimeout({
        store,
        source: [source],
      });
    }
  }, [loginError, source, store]);

  useEffect(() => {
    if (
      isHandoffFlow &&
      store &&
      janeUser?.user.authenticated &&
      !hasSignedIn.current
    ) {
      hasSignedIn.current = true;
      trackSignedIn({
        source: [source],
        ssoChannel: null,
        store,
      });
    }
  }, [isHandoffFlow, source, store, janeUser]);

  const handleOnLinkBank = () => {
    const step = needsMoreInfoForLinkBank
      ? SCREEN_NAMES.moreInfo
      : SCREEN_NAMES.aeropay;

    trackLinkBankAccountTap({
      deviceType: 'Mobile',
      source: [source],
      store,
    });
    navigate(
      `/stores/${storeId}/mobile/link-bank?step=${step}&source=${source}`,
      { state: { fromMobile: true } }
    );
  };

  useWindowListener('beforeunload', handleBeforeUnload);

  useEffect(() => {
    if (isUserFetching || screen) {
      return;
    }

    if (janeUser?.user.authenticated) {
      // The Roots handoff already authorized the device grant, so there is no
      // user_code left to authorize - go straight to the post sign in screen.
      handleSetScreen(isHandoffFlow ? 'success' : 'confirmation');
    } else if (!isHandoffFlow) {
      // A handoff never falls back to the sign in form: the user authenticated
      // externally, and externalAuthLoader has already sent a failed exchange
      // to the expired link page.
      handleSetScreen('sign-in');
    }
  }, [isUserFetching, screen, handleSetScreen, janeUser, isHandoffFlow]);

  // identify launch darkly user context on jdid or user change for proper flag targeting
  useEffect(() => {
    async function identifyContext() {
      await flagClient?.identify(
        getFlagContext({
          extraAttributes: {
            appMode: AppMode.Kiosk,
            state: statePostalCode(store?.state),
            storeId: store?.id,
            organizationId: store?.organization_id ?? undefined,
          },
          janeDeviceId: jdid,
          user: { ...janeUser?.user },
        })
      );
    }
    identifyContext();
  }, [flagClient, store, jdid, janeUser]);

  return (
    <LoadingWrapper isLoading={!screen} variant="full-page">
      {(screen === 'sign-in' || screen === 'create-account') && (
        <SignInToggleButtons screen={screen} setScreen={handleSetScreen} />
      )}

      {screen === 'sign-in' && (
        <SignInScreen
          storeName={store.name}
          onSubmit={signInSubmit}
          onForgotPassword={() => {
            setForgotPasswordScreen('form');
            handleSetScreen('forgot-password');
          }}
          onAppleClick={onAppleLogin}
          onGoogleClick={onGoogleLogin}
          error={loginError}
        />
      )}

      {screen === 'create-account' && (
        <CreateAccountScreen
          onSubmit={createAccountSubmit}
          onAppleClick={onAppleLogin}
          onGoogleClick={onGoogleLogin}
          errors={registerErrors}
        />
      )}

      {screen === 'forgot-password' && (
        <ForgotPasswordScreen
          error={resetPasswordError}
          forgotPasswordScreen={forgotPasswordScreen}
          onBack={() => handleSetScreen('sign-in')}
          onSubmit={resetPasswordSubmit}
        />
      )}

      {screen?.includes('success') && (
        <SuccessScreen
          storeName={store.name}
          userName={userName ?? ''}
          showJaneGold={showGold}
          loadingBanks={isFetchingAeropayData}
          hasLinkedBank={userHasLinkedBank}
          newUser={screen === 'new-success'}
          showJanePay={showJanePay}
          hideBranding={Boolean(store?.jane_pay_integration?.white_label)}
          onLinkBank={handleOnLinkBank}
        />
      )}

      {screen === 'confirmation' && (
        <ConfirmationScreen
          userName={userName ?? ''}
          storeName={store.name}
          loginOnKiosk={kioskDeviceAuthorization}
          logoutUser={logoutUser}
          error={loginError}
        />
      )}
    </LoadingWrapper>
  );
};
